import { useQuery } from "@tanstack/react-query";
import type {
  GridDocumentDetailsResponse,
  OpinionDocumentDetailsResponse,
  PageDocumentDetailsResponse,
  ProductEnablementData,
  ProductEnablementResponse,
} from "../types/posthogTypes";
// import { queryClient } from "../index";
import { keyFactory } from "../util/keyFactory";
import { logger } from '../util/logger';
import { HttpClient } from "./axiosConfig";
interface FreeTrialDetailsData {
  freeTrialUserId: null;
  freeTrialId: null;
  userId: null;
  isMailSent: null;
  lastMailSentDate: null;
  renderStyleId: null;
  startDate: null;
  endDate: null;
  orgId: null;
  description: null;
  freeTrialCount: number;
  minStartDate?: string | null;
  maxEndDate?: string | null;
}
type FreeTrialDetailsResponse = GlobalResponse<FreeTrialDetailsData>;

const getFreeTrialQueryFn = async (userId: number, orgId: number): Promise<FreeTrialDetailsResponse> => {
  const result = await HttpClient.get<FreeTrialDetailsResponse>(
    `/users/user-free-trial-detail?userId=${userId}&orgId=${orgId}`
  );
  return result.data;
};

export const useGetFreeTrialDetails = (userId: number, orgId: number) => {
  return useQuery({
    queryKey: [keyFactory.getFreeTrialDetails, userId, orgId],
    queryFn: () => getFreeTrialQueryFn(userId, orgId),
    enabled: Boolean(userId) && Boolean(orgId),
  });
};

export const getFreeTrialDetails = async (userId: number, orgId: number): Promise<FreeTrialDetailsData | null> => {
  if (!userId || !orgId) {
    return null;
  }
  try {
    const result = await getFreeTrialQueryFn(userId, orgId);
    return result?.data ?? null;
  } catch (error) {
    logger.error('Failed to get free trial details:', error);
    return null;
  }
};

export const getProductEnablement = async (): Promise<ProductEnablementData | null> => {
  try {
    const result = await HttpClient.get<ProductEnablementResponse>(
      '/users/products/enablement'
    );
    return result.data?.data ?? null;
  } catch (error) {
    logger.error('Failed to get product enablement:', error);
    return null;
  }
};

export const useGetOpinionDocumentDetails = (reportId: string, documentId: string) => {
  return useQuery({
    queryKey: [keyFactory.getOpinionDocumentDetails, reportId , documentId],
    queryFn: async () => {
      const result = await HttpClient.get<OpinionDocumentDetailsResponse>(
        `/users/reports/document-details?reportId=${reportId}&docId=${documentId}&resource=0`
      );
      return result.data.data ?? null;
    },
    enabled: !!reportId && reportId !== '0' && reportId !== 'null' && !!documentId && documentId !== '0' && documentId !== 'null',
  });
};

export const useGetPageDocumentDetails = (pageId: string, documentId: string) => {
  return useQuery({
    queryKey: [keyFactory.getPageDocumentDetails, pageId , documentId],
    queryFn: async () => {
      const result = await HttpClient.get<PageDocumentDetailsResponse>(
        `/users/pages/document-details?pageId=${pageId}&docId=${documentId}&isResource=0`
      );
      return result.data.data ?? null;
    },
    enabled: !!pageId && pageId !== '0' && pageId !== 'null' && !!documentId && documentId !== '0' && documentId !== 'null',
  });
};

export const useGetGridDocumentDetails = (gridId: string, documentId: string) => {
  return useQuery({
    queryKey: [keyFactory.getGridDocumentDetails, gridId , documentId],
    queryFn: async () => {
      const result = await HttpClient.get<GridDocumentDetailsResponse>(
        `/users/grids/document-details?gridId=${gridId}&docId=${documentId}&resource=0`
      );
      return result.data.data ?? null;
    },
    enabled: !!gridId && gridId !== '0' && gridId !== 'null' && !!documentId && documentId !== '0' && documentId !== 'null',
  });
};